import React, { useState, useEffect, useContext } from "react";
import { Grid } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import useSetTitlePage from "../../hooks/useSetTitlePage";
import CustomLabel from "../../components/common/customLabel";
import BalanceSection from "../../components/profile/BalanceSection";
import AutorizacionesList from "../../components/medicamentos/AutorizacionesList";
import DetalleAutorizacionModal from "../../components/medicamentos/DetalleAutorizacionModal";
import { Divider } from "@mui/material";
import { labelTitlePrestador } from "../../global/styles";
import { ConfigurationContext } from "../../context/configurationContext";
import { setInfoPerfilUsuario } from "../../redux/features/afiliado/afiliadoSlice";
import * as apiService from "../../../api";

const Medicamentos = () => {
  // Estados para autorizaciones
  const [autorizaciones, setAutorizaciones] = useState([]);
  const [loadingAutorizaciones, setLoadingAutorizaciones] = useState(true);

  // Estados para el modal de detalle
  const [openDetalleModal, setOpenDetalleModal] = useState(false);
  const [detalleAutorizacion, setDetalleAutorizacion] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [autorizacionSeleccionada, setAutorizacionSeleccionada] =
    useState(null);

  // Ref para controlar si el componente está montado
  const isMountedRef = React.useRef(true);

  // Context, Redux y Dispatch
  const dispatch = useDispatch();
  const { esDispositivoActualSeguro } = useContext(ConfigurationContext);
  const [esDispositivoSeguro, setEsDispositivoSeguro] = useState(
    esDispositivoActualSeguro()
  );
  const perfilUsuario = useSelector(
    (state) => state.afiliado.infoPerfilUsuario
  );
  const infoAfiliado = useSelector((state) => state.afiliado.infoAfiliado);
  const featureFlags = useSelector((state) => state.configuration.feauteFlags);
  const [datos, setDatos] = useState(perfilUsuario);
  const [infoGeneralActual, setInfoGeneralActual] = useState(
    datos.datosAfiliacion ? datos.datosAfiliacion[0] : null
  );

  // Configuración del título de la página
  const TitleCobertura = (
    <>
      <CustomLabel style={{ ...labelTitlePrestador }}>Medicamentos</CustomLabel>
      <Divider />
      <br />
      <br />
    </>
  );

  useSetTitlePage(TitleCobertura);

  // Efectos
  useEffect(() => {
    if (isMountedRef.current) {
      setDatos(perfilUsuario);
    }
  }, [perfilUsuario]);

  useEffect(() => {
    // Actualizar infoGeneralActual cuando datos cambia - IGUAL que en tabDatoAfiliacion.jsx
    if (isMountedRef.current && datos?.datosAfiliacion) {
      const nuevoInfoGeneral = datos.datosAfiliacion[0];
      setInfoGeneralActual(nuevoInfoGeneral);
    }
  }, [datos]);

  // Efecto para cargar datos del afiliado si no están disponibles
  useEffect(() => {
    const cargarDatosAfiliado = async () => {
      // Si no tenemos perfilUsuario completo pero sí tenemos infoAfiliado
      if (
        infoAfiliado?.codigoAfiliado &&
        (!perfilUsuario || !perfilUsuario.datosPersonales)
      ) {
        try {
          const response = await apiService.afiliado.getAfiliado(
            infoAfiliado.codigoAfiliado
          );

          if (response?.data?.result && isMountedRef.current) {
            dispatch(setInfoPerfilUsuario(response.data.result));
          } else {
            console.error("No se obtuvieron datos del afiliado");
          }
        } catch (error) {
          console.error("Error al cargar datos del afiliado:", error);
        }
      }
    };

    cargarDatosAfiliado();
  }, [infoAfiliado, perfilUsuario, dispatch]);

  useEffect(() => {
    const fetchAutorizaciones = async () => {
      const codigoAfiliado =
        infoAfiliado?.codigoAfiliado ||
        perfilUsuario?.datosPersonales?.codigoAfiliado;

      console.log(
        "fetchAutorizaciones ejecutándose con código:",
        codigoAfiliado
      );

      if (!codigoAfiliado) {
        console.log("No hay código de afiliado para cargar autorizaciones");
        if (isMountedRef.current) {
          setLoadingAutorizaciones(false);
        }
        return;
      }

      try {
        if (isMountedRef.current) {
          setLoadingAutorizaciones(true);
          console.log("Cargando autorizaciones para código:", codigoAfiliado);
        }

        const response =
          await apiService.autorizacion.getAutorizacionesMedicamentosAfiliado(
            // 123459 hardcodeado
            codigoAfiliado
          );

        console.log("Respuesta de autorizaciones:", response);

        if (response?.data?.result && isMountedRef.current) {
          console.log("Autorizaciones cargadas:", response.data.result.length);
          setAutorizaciones(response.data.result);
        } else {
          console.log("No se encontraron autorizaciones");
          if (isMountedRef.current) {
            setAutorizaciones([]);
          }
        }
      } catch (err) {
        console.error("Error al cargar autorizaciones:", err);
        if (isMountedRef.current) {
          setAutorizaciones([]);
        }
      } finally {
        if (isMountedRef.current) {
          setLoadingAutorizaciones(false);
        }
      }
    };

    fetchAutorizaciones();
  }, [
    infoAfiliado?.codigoAfiliado,
    perfilUsuario?.datosPersonales?.codigoAfiliado,
  ]);

  // Handler para cuando cambia el estado de dispositivo seguro
  const handleDispositivoSeguroChange = (isSeguro) => {
    if (isMountedRef.current) {
      setEsDispositivoSeguro(isSeguro);
      console.log("Dispositivo seguro actualizado:", isSeguro);
    }
  };

  // Handler para abrir el detalle de la autorización
  const handleAutorizacionClick = async (autorizacion) => {
    console.log("Autorización seleccionada:", autorizacion);
    setAutorizacionSeleccionada(autorizacion);
    setOpenDetalleModal(true);
    setLoadingDetalle(true);

    try {
      const response =
        await apiService.autorizacion.getDetalleAutorizacionMedicamentos(
          autorizacion.idAuth
        );

      if (response?.data?.result && isMountedRef.current) {
        setDetalleAutorizacion(response.data.result);
      } else {
        setDetalleAutorizacion([]);
      }
    } catch (error) {
      console.error("Error al cargar detalle de autorización:", error);
      if (isMountedRef.current) {
        setDetalleAutorizacion([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingDetalle(false);
      }
    }
  };

  // Handler para cerrar el modal de detalle
  const handleCloseDetalleModal = () => {
    setOpenDetalleModal(false);
    setDetalleAutorizacion([]);
    setAutorizacionSeleccionada(null);
  };

  return (
    <>
      <Grid container paddingX={5} paddingTop={2} spacing={2}>
        {/* Sección de Balance de Medicamentos - IGUAL que en tabDatoAfiliacion.jsx */}
        {infoGeneralActual &&
        infoGeneralActual.balances &&
        datos &&
        featureFlags?.disponibleMedicamentos &&
        infoGeneralActual.balances.length > 0 ? (
          <BalanceSection
            infoGeneralActual={infoGeneralActual}
            datos={datos}
            esDispositivoSeguro={esDispositivoSeguro}
            onDispositivoSeguroChange={handleDispositivoSeguroChange}
            title={esDispositivoSeguro ? "Mis planes" : ""}
            mostrarPlan={true}
            esDatosAfiliacionBalances={true}
          />
        ) : (
          <></>
        )}

        {/* Sección de Autorizaciones */}
        <Grid item xs={12}>
          {esDispositivoSeguro && (
            <AutorizacionesList
              autorizaciones={autorizaciones}
              loading={loadingAutorizaciones}
              title="Últimos consumos"
              onAutorizacionClick={handleAutorizacionClick}
              customStyles={{
                title: {
                  fontSize: "22px",
                  fontWeight: "600",
                },
              }}
            />
          )}
        </Grid>
      </Grid>

      {/* Modal de Detalle de Autorización */}
      <DetalleAutorizacionModal
        open={openDetalleModal}
        onClose={handleCloseDetalleModal}
        detalle={detalleAutorizacion}
        loading={loadingDetalle}
        authNumber={autorizacionSeleccionada?.auth}
      />
    </>
  );
};

export default Medicamentos;
